import { useState, useMemo, useEffect } from 'react'
import { Link2, Copy, ExternalLink, X, Sparkles, Globe, History, QrCode, Download, CheckCircle2, Zap } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useLanguage } from '../contexts/LanguageContext'
import { Helmet } from 'react-helmet-async'
import FAQAccordion from '../components/FAQAccordion'

const UTMLinkGenerator = () => {
  const { language } = useLanguage()
  const [websiteUrl, setWebsiteUrl] = useState('https://example.com')
  const [campaignSource, setCampaignSource] = useState('')
  const [campaignMedium, setCampaignMedium] = useState('')
  const [campaignName, setCampaignName] = useState('')
  const [campaignTerm, setCampaignTerm] = useState('')
  const [campaignContent, setCampaignContent] = useState('')
  const [generatedLink, setGeneratedLink] = useState('')
  const [copied, setCopied] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [linkHistory, setLinkHistory] = useState<string[]>([])
  const [showQR, setShowQR] = useState(false)

  const translations = {
    tr: {
      title: 'Ücretsiz UTM Link Oluşturucu',
      seoTitle: 'Ücretsiz UTM Link Oluşturucu | İstanbul, Türkiye | Thinklyze',
      seoDescription: 'Ücretsiz UTM link oluşturucu aracı ile kampanya takibi için UTM parametreli linkler oluşturun. Google Analytics entegrasyonu. İstanbul ve Türkiye genelinde kullanılabilir. Tamamen ücretsiz.',
      seoKeywords: 'utm link oluşturucu, utm link generator, ücretsiz utm link, utm parametreleri, kampanya takibi, google analytics utm, istanbul utm link, türkiye utm link',
      description: 'Kampanya takibi için UTM parametreli linkler oluşturun',
      websiteUrlLabel: 'Website URL',
      websiteUrlPlaceholder: 'https://example.com',
      campaignSourceLabel: 'Kampanya Kaynağı',
      campaignSourcePlaceholder: 'google, facebook, newsletter',
      campaignSourceHint: 'Trafiğin nereden geldiği (google, facebook, vb.)',
      campaignMediumLabel: 'Kampanya Medyası',
      campaignMediumPlaceholder: 'Medya türünü seçin',
      campaignNameLabel: 'Kampanya Adı',
      campaignNamePlaceholder: 'spring_sale, product_launch',
      campaignTermLabel: 'Kampanya Terimi (İsteğe Bağlı)',
      campaignTermPlaceholder: 'anahtar+kelime',
      campaignTermHint: 'Paid search için anahtar kelimeler',
      campaignContentLabel: 'Kampanya İçeriği (İsteğe Bağlı)',
      campaignContentPlaceholder: 'banner_top, text_link',
      campaignContentHint: 'Aynı reklamın farklı versiyonlarını ayırt etmek için',
      generateButton: 'UTM Linki Oluştur',
      clearButton: 'Temizle',
      generatedTitle: 'Oluşturulan UTM Linki',
      copyButton: 'Kopyala',
      testButton: 'Test Et',
      copied: 'Kopyalandı!',
      invalidFields: 'Lütfen zorunlu alanları doldurun',
      previewLabel: 'Canlı Önizleme',
      previewHint: 'Link oluşturmadan önce önizleyin',
      historyLabel: 'Link Geçmişi',
      historyEmpty: 'Henüz link oluşturulmadı',
      historyClear: 'Geçmişi Temizle',
      qrLabel: 'QR Kod Oluştur',
      qrDownload: 'QR Kodu İndir',
      templateLabel: 'Hızlı Şablonlar',
      templates: {
        googleAds: 'Google Ads',
        facebook: 'Facebook',
        instagram: 'Instagram',
        email: 'Email Kampanyası',
        newsletter: 'Newsletter',
        twitter: 'Twitter'
      },
      paramsLabel: 'UTM Parametreleri',
      faqTitle: 'Sık Sorulan Sorular',
      faqs: [
        {
          question: 'UTM link oluşturucu ücretsiz mi?',
          answer: 'Evet, UTM link oluşturucu aracımız tamamen ücretsizdir. Sınırsız sayıda UTM linki oluşturabilir ve kampanyalarınızı takip edebilirsiniz.'
        },
        {
          question: 'UTM parametreleri ne işe yarar?',
          answer: 'UTM parametreleri, trafiğinizin nereden geldiğini, hangi kampanyadan geldiğini ve hangi içerikten geldiğini Google Analytics ve diğer analitik araçlarda takip etmenizi sağlar.'
        },
        {
          question: 'Hangi UTM parametreleri zorunludur?',
          answer: 'utm_source (kaynak), utm_medium (medya) ve utm_campaign (kampanya adı) zorunludur. utm_term ve utm_content isteğe bağlıdır.'
        },
        {
          question: 'UTM linklerimi Google Analytics\'te nasıl görürüm?',
          answer: 'Oluşturduğunuz UTM linklerini kullandıktan sonra, Google Analytics\'te Acquisition > Campaigns bölümünden kampanyalarınızı ve trafik kaynaklarınızı görüntüleyebilirsiniz.'
        },
        {
          question: 'UTM linklerim ne kadar süre geçerlidir?',
          answer: 'UTM linkleriniz süresiz olarak geçerlidir. Website URL\'iniz değişmediği sürece linkler her zaman çalışacaktır.'
        }
      ],
      mediaTypes: {
        cpc: 'CPC (Paid Search)',
        cpm: 'CPM (Display)',
        social: 'Social',
        email: 'Email',
        organic: 'Organic',
        referral: 'Referral',
        direct: 'Direct'
      }
    },
    en: {
      title: 'Free UTM Link Generator',
      seoTitle: 'Free UTM Link Generator | Istanbul, Turkey | Thinklyze',
      seoDescription: 'Create UTM parameterized links for campaign tracking with our free UTM link generator tool. Google Analytics integration. Available in Istanbul and throughout Turkey. Completely free.',
      seoKeywords: 'utm link generator, free utm link, utm parameters, campaign tracking, google analytics utm, istanbul utm link, turkey utm link',
      description: 'Create UTM parameterized links for campaign tracking',
      websiteUrlLabel: 'Website URL',
      websiteUrlPlaceholder: 'https://example.com',
      campaignSourceLabel: 'Campaign Source',
      campaignSourcePlaceholder: 'google, facebook, newsletter',
      campaignSourceHint: 'Where the traffic came from (google, facebook, etc.)',
      campaignMediumLabel: 'Campaign Medium',
      campaignMediumPlaceholder: 'Select media type',
      campaignNameLabel: 'Campaign Name',
      campaignNamePlaceholder: 'spring_sale, product_launch',
      campaignTermLabel: 'Campaign Term (Optional)',
      campaignTermPlaceholder: 'keyword+phrase',
      campaignTermHint: 'Keywords for paid search',
      campaignContentLabel: 'Campaign Content (Optional)',
      campaignContentPlaceholder: 'banner_top, text_link',
      campaignContentHint: 'To distinguish different versions of the same ad',
      generateButton: 'Create UTM Link',
      clearButton: 'Clear',
      generatedTitle: 'Generated UTM Link',
      copyButton: 'Copy',
      testButton: 'Test',
      copied: 'Copied!',
      invalidFields: 'Please fill in the required fields',
      previewLabel: 'Live Preview',
      previewHint: 'Preview before generating link',
      historyLabel: 'Link History',
      historyEmpty: 'No links created yet',
      historyClear: 'Clear History',
      qrLabel: 'Generate QR Code',
      qrDownload: 'Download QR Code',
      templateLabel: 'Quick Templates',
      templates: {
        googleAds: 'Google Ads',
        facebook: 'Facebook',
        instagram: 'Instagram',
        email: 'Email Campaign',
        newsletter: 'Newsletter',
        twitter: 'Twitter'
      },
      paramsLabel: 'UTM Parameters',
      faqTitle: 'Frequently Asked Questions',
      faqs: [
        {
          question: 'Is the UTM link generator free?',
          answer: 'Yes, our UTM link generator tool is completely free. You can create unlimited UTM links and track your campaigns.'
        },
        {
          question: 'What do UTM parameters do?',
          answer: 'UTM parameters allow you to track where your traffic comes from, which campaign it came from, and which content it came from in Google Analytics and other analytics tools.'
        },
        {
          question: 'Which UTM parameters are required?',
          answer: 'utm_source (source), utm_medium (medium), and utm_campaign (campaign name) are required. utm_term and utm_content are optional.'
        },
        {
          question: 'How do I see my UTM links in Google Analytics?',
          answer: 'After using the generated UTM links, you can view your campaigns and traffic sources in Google Analytics under Acquisition > Campaigns.'
        },
        {
          question: 'How long are my UTM links valid?',
          answer: 'Your UTM links are valid indefinitely. As long as your website URL doesn\'t change, the links will always work.'
        }
      ],
      mediaTypes: {
        cpc: 'CPC (Paid Search)',
        cpm: 'CPM (Display)',
        social: 'Social',
        email: 'Email',
        organic: 'Organic',
        referral: 'Referral',
        direct: 'Direct'
      }
    },
    ru: {
      title: 'Бесплатный генератор UTM-ссылок',
      seoTitle: 'Бесплатный генератор UTM-ссылок | Стамбул, Турция | Thinklyze',
      seoDescription: 'Создавайте ссылки с UTM-параметрами для отслеживания кампаний с помощью нашего бесплатного инструмента. Интеграция с Google Analytics. Доступно в Стамбуле и по всей Турции. Полностью бесплатно.',
      seoKeywords: 'генератор utm ссылок, бесплатная utm ссылка, utm параметры, отслеживание кампаний, google analytics utm, стамбул utm ссылка, турция utm ссылка',
      description: 'Создавайте ссылки с UTM-параметрами для отслеживания кампаний',
      websiteUrlLabel: 'URL веб-сайта',
      websiteUrlPlaceholder: 'https://example.com',
      campaignSourceLabel: 'Источник кампании',
      campaignSourcePlaceholder: 'google, facebook, newsletter',
      campaignSourceHint: 'Откуда пришел трафик (google, facebook и т.д.)',
      campaignMediumLabel: 'Медиа кампании',
      campaignMediumPlaceholder: 'Выберите тип медиа',
      campaignNameLabel: 'Название кампании',
      campaignNamePlaceholder: 'spring_sale, product_launch',
      campaignTermLabel: 'Термин кампании (необязательно)',
      campaignTermPlaceholder: 'ключевое+слово',
      campaignTermHint: 'Ключевые слова для платного поиска',
      campaignContentLabel: 'Содержание кампании (необязательно)',
      campaignContentPlaceholder: 'banner_top, text_link',
      campaignContentHint: 'Для различения разных версий одной рекламы',
      generateButton: 'Создать UTM-ссылку',
      clearButton: 'Очистить',
      generatedTitle: 'Созданная UTM-ссылка',
      copyButton: 'Копировать',
      testButton: 'Тест',
      copied: 'Скопировано!',
      invalidFields: 'Пожалуйста, заполните обязательные поля',
      previewLabel: 'Предварительный просмотр',
      previewHint: 'Просмотрите перед созданием ссылки',
      historyLabel: 'История ссылок',
      historyEmpty: 'Ссылки еще не созданы',
      historyClear: 'Очистить историю',
      qrLabel: 'Создать QR-код',
      qrDownload: 'Скачать QR-код',
      templateLabel: 'Быстрые шаблоны',
      templates: {
        googleAds: 'Google Ads',
        facebook: 'Facebook',
        instagram: 'Instagram',
        email: 'Email кампания',
        newsletter: 'Newsletter',
        twitter: 'Twitter'
      },
      paramsLabel: 'UTM параметры',
      mediaTypes: {
        cpc: 'CPC (Платный поиск)',
        cpm: 'CPM (Баннерная реклама)',
        social: 'Социальные сети',
        email: 'Email',
        organic: 'Органический',
        referral: 'Реферальный',
        direct: 'Прямой'
      },
      faqTitle: 'Часто задаваемые вопросы',
      faqs: [
        {
          question: 'Генератор UTM-ссылок бесплатный?',
          answer: 'Да, наш инструмент генератора UTM-ссылок полностью бесплатный. Вы можете создавать неограниченное количество UTM-ссылок и отслеживать свои кампании.'
        },
        {
          question: 'Что делают UTM-параметры?',
          answer: 'UTM-параметры позволяют отслеживать, откуда приходит ваш трафик, из какой кампании он пришел и из какого контента в Google Analytics и других аналитических инструментах.'
        },
        {
          question: 'Какие UTM-параметры обязательны?',
          answer: 'utm_source (источник), utm_medium (медиа) и utm_campaign (название кампании) обязательны. utm_term и utm_content необязательны.'
        },
        {
          question: 'Как я вижу свои UTM-ссылки в Google Analytics?',
          answer: 'После использования созданных UTM-ссылок вы можете просматривать свои кампании и источники трафика в Google Analytics в разделе Acquisition > Campaigns.'
        },
        {
          question: 'Как долго действительны мои UTM-ссылки?',
          answer: 'Ваши UTM-ссылки действительны бессрочно. Пока URL вашего веб-сайта не изменится, ссылки всегда будут работать.'
        }
      ]
    }
  }

  const t = translations[language as keyof typeof translations] || translations.tr

  // SEO Schema Markup
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'tr' ? 'Ana Sayfa' : language === 'en' ? 'Home' : 'Главная',
        item: 'https://thinklyze.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: language === 'tr' ? 'Hizmetler' : language === 'en' ? 'Services' : 'Услуги',
        item: 'https://thinklyze.com/services'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t.title,
        item: 'https://thinklyze.com/utm-link-generator'
      }
    ]
  }), [language, t.title])

  const howToSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t.title,
    description: t.description,
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: language === 'tr' ? 'Website URL\'inizi girin' : language === 'en' ? 'Enter your website URL' : 'Введите URL вашего веб-сайта',
        text: language === 'tr' ? 'Website URL alanına UTM linki oluşturmak istediğiniz web sitesinin URL\'ini girin.' : language === 'en' ? 'Enter the URL of the website you want to create a UTM link for in the website URL field.' : 'Введите URL веб-сайта, для которого вы хотите создать UTM-ссылку, в поле URL веб-сайта.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: language === 'tr' ? 'Kampanya bilgilerini doldurun' : language === 'en' ? 'Fill in campaign information' : 'Заполните информацию о кампании',
        text: language === 'tr' ? 'Kampanya kaynağı, medyası ve adını girin. İsteğe bağlı olarak kampanya terimi ve içeriği de ekleyebilirsiniz.' : language === 'en' ? 'Enter campaign source, medium, and name. You can also optionally add campaign term and content.' : 'Введите источник кампании, медиа и название. Вы также можете при желании добавить термин кампании и содержание.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: language === 'tr' ? 'UTM linkini oluşturun' : language === 'en' ? 'Generate the UTM link' : 'Создайте UTM-ссылку',
        text: language === 'tr' ? '\'UTM Linki Oluştur\' butonuna tıklayın ve UTM linkinizi alın.' : language === 'en' ? 'Click the \'Create UTM Link\' button and get your UTM link.' : 'Нажмите кнопку \'Создать UTM-ссылку\' и получите свою UTM-ссылку.'
      }
    ]
  }), [language, t.title, t.description])

  const faqSchema = useMemo(() => {
    if (!t.faqs || t.faqs.length === 0) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: t.faqs.map((faq: any) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  }, [t.faqs])

  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t.seoTitle || t.title,
    description: t.seoDescription || t.description,
    url: 'https://thinklyze.com/utm-link-generator',
    inLanguage: language === 'tr' ? 'tr-TR' : language === 'en' ? 'en-US' : 'ru-RU',
    about: {
      '@type': 'Thing',
      name: 'UTM Link Generator',
      description: t.description
    },
    provider: {
      '@type': 'Organization',
      name: 'Thinklyze',
      url: 'https://thinklyze.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bakırköy',
        addressRegion: 'İstanbul',
        addressCountry: 'TR'
      }
    }
  }), [language, t])

  // Link geçmişini localStorage'dan yükle
  useEffect(() => {
    const saved = localStorage.getItem('utm-link-history')
    if (saved) {
      try {
        setLinkHistory(JSON.parse(saved))
      } catch (e) {
        console.error('Geçmiş yüklenemedi:', e)
      }
    }
  }, [])

  // Template fonksiyonları
  const applyTemplate = (template: string) => {
    const templates: Record<string, { source: string; medium: string; name: string }> = {
      googleAds: { source: 'google', medium: 'cpc', name: 'google_ads_campaign' },
      facebook: { source: 'facebook', medium: 'social', name: 'facebook_campaign' },
      instagram: { source: 'instagram', medium: 'social', name: 'instagram_campaign' },
      email: { source: 'email', medium: 'email', name: 'email_campaign' },
      newsletter: { source: 'newsletter', medium: 'email', name: 'newsletter_campaign' },
      twitter: { source: 'twitter', medium: 'social', name: 'twitter_campaign' }
    }

    const templateData = templates[template]
    if (templateData) {
      setCampaignSource(templateData.source)
      setCampaignMedium(templateData.medium)
      setCampaignName(templateData.name)
    }
  }

  // UTM link oluşturma fonksiyonu
  const buildUTMLink = () => {
    // URL'i temizle ve doğrula
    let url = websiteUrl.trim()
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }

    // UTM parametrelerini oluştur
    const params = new URLSearchParams()
    params.append('utm_source', campaignSource.trim())
    params.append('utm_medium', campaignMedium.trim())
    params.append('utm_campaign', campaignName.trim())

    if (campaignTerm.trim()) {
      params.append('utm_term', campaignTerm.trim())
    }

    if (campaignContent.trim()) {
      params.append('utm_content', campaignContent.trim())
    }

    // URL'i birleştir
    const separator = url.includes('?') ? '&' : '?'
    return url + separator + params.toString()
  }

  const generateUTMLink = () => {
    // Zorunlu alanları kontrol et
    if (!websiteUrl.trim() || !campaignSource.trim() || !campaignMedium.trim() || !campaignName.trim()) {
      alert(t.invalidFields)
      return
    }

    const utmLink = buildUTMLink()
    setGeneratedLink(utmLink)
    
    // Link geçmişine ekle
    const newHistory = [utmLink, ...linkHistory.filter(link => link !== utmLink)].slice(0, 10)
    setLinkHistory(newHistory)
    localStorage.setItem('utm-link-history', JSON.stringify(newHistory))
  }

  // UTM parametrelerini parse et
  const parseUTMParams = (link: string) => {
    try {
      const url = new URL(link)
      const params: Record<string, string> = {}
      url.searchParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          params[key] = value
        }
      })
      return params
    } catch {
      return {}
    }
  }

  const clearAll = () => {
    setWebsiteUrl('https://example.com')
    setCampaignSource('')
    setCampaignMedium('')
    setCampaignName('')
    setCampaignTerm('')
    setCampaignContent('')
    setGeneratedLink('')
    setCopied(false)
    setShowPreview(false)
    setShowQR(false)
  }

  const clearHistory = () => {
    setLinkHistory([])
    localStorage.removeItem('utm-link-history')
  }

  const useHistoryLink = (link: string) => {
    setGeneratedLink(link)
    const params = parseUTMParams(link)
    const url = new URL(link)
    setWebsiteUrl(url.origin + url.pathname)
    setCampaignSource(params.utm_source || '')
    setCampaignMedium(params.utm_medium || '')
    setCampaignName(params.utm_campaign || '')
    setCampaignTerm(params.utm_term || '')
    setCampaignContent(params.utm_content || '')
  }

  const downloadQR = () => {
    if (!generatedLink) return
    
    const svg = document.querySelector('#qr-code svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgData], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `utm-qrcode-${Date.now()}.svg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const copyToClipboard = async () => {
    if (!generatedLink) return

    try {
      await navigator.clipboard.writeText(generatedLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Kopyalama hatası:', err)
    }
  }

  const testLink = () => {
    if (generatedLink) {
      window.open(generatedLink, '_blank')
    }
  }

  return (
    <>
      <Helmet>
        <title>{t.seoTitle || t.title} | Thinklyze</title>
        <meta name="description" content={t.seoDescription || t.description} />
        <meta name="keywords" content={t.seoKeywords} />
        <link rel="canonical" href="https://thinklyze.com/utm-link-generator" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={t.seoTitle || t.title} />
        <meta property="og:description" content={t.seoDescription || t.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinklyze.com/utm-link-generator" />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : language === 'en' ? 'en_US' : 'ru_RU'} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.seoTitle || t.title} />
        <meta name="twitter:description" content={t.seoDescription || t.description} />
        <meta name="twitter:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <meta name="ICBM" content="41.0082, 28.9784" />
      </Helmet>
      
      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      
      <div className="pt-16 bg-[#f9f9f9] relative min-h-screen">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 relative z-10">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 mb-4 sm:mb-6">
              <Sparkles className="w-4 h-4 text-green-600" />
              <span className="text-xs sm:text-[13px] font-medium text-gray-600 bg-white px-3 sm:px-4 py-1.5 rounded-full border border-gray-200 shadow-sm">
                {language === 'tr' ? 'Ücretsiz Araç' : language === 'en' ? 'Free Tool' : 'Бесплатный инструмент'}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-green-600 opacity-20 blur-xl rounded-full"></div>
                <Link2 className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 relative z-10" />
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                {t.title}
              </h1>
            </div>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-3 sm:mb-4 leading-relaxed px-4 sm:px-0">
              {t.description}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 max-w-2xl mx-auto px-4 sm:px-0">
              {language === 'tr' 
                ? 'İstanbul, Türkiye\'de dijital pazarlama profesyonelleri için ücretsiz araç' 
                : language === 'en' 
                ? 'Free tool for digital marketing professionals in Istanbul, Turkey' 
                : 'Бесплатный инструмент для специалистов по цифровому маркетингу в Стамбуле, Турция'}
            </p>
          </div>

          {/* Quick Templates */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 mb-6 sm:mb-8 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-green-600" />
              <h3 className="text-base font-bold text-gray-900">{t.templateLabel}</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {Object.entries(t.templates).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => applyTemplate(key)}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all duration-200 text-xs sm:text-sm font-medium text-gray-700"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 mb-6 sm:mb-8 border border-gray-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300">
            {/* Website URL */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="websiteUrl" className="block text-sm font-semibold text-gray-900 mb-2">
                {t.websiteUrlLabel} *
              </label>
              <input
                type="text"
                id="websiteUrl"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder={t.websiteUrlPlaceholder}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-base sm:text-lg bg-gray-50 focus:bg-white"
              />
            </div>

            {/* Campaign Source */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="campaignSource" className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <Globe className="w-4 h-4 text-green-600" />
                {t.campaignSourceLabel} *
              </label>
              <input
                type="text"
                id="campaignSource"
                value={campaignSource}
                onChange={(e) => setCampaignSource(e.target.value)}
                placeholder={t.campaignSourcePlaceholder}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              />
              <p className="text-xs text-gray-500 mt-2">{t.campaignSourceHint}</p>
            </div>

            {/* Campaign Medium */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="campaignMedium" className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                {t.campaignMediumLabel} *
              </label>
              <select
                id="campaignMedium"
                value={campaignMedium}
                onChange={(e) => setCampaignMedium(e.target.value)}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              >
                <option value="">{t.campaignMediumPlaceholder}</option>
                <option value="cpc">{t.mediaTypes.cpc}</option>
                <option value="cpm">{t.mediaTypes.cpm}</option>
                <option value="social">{t.mediaTypes.social}</option>
                <option value="email">{t.mediaTypes.email}</option>
                <option value="organic">{t.mediaTypes.organic}</option>
                <option value="referral">{t.mediaTypes.referral}</option>
                <option value="direct">{t.mediaTypes.direct}</option>
              </select>
            </div>

            {/* Campaign Name */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="campaignName" className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                {t.campaignNameLabel} *
              </label>
              <input
                type="text"
                id="campaignName"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder={t.campaignNamePlaceholder}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              />
            </div>

            {/* Campaign Term (Optional) */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="campaignTerm" className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                {t.campaignTermLabel}
              </label>
              <input
                type="text"
                id="campaignTerm"
                value={campaignTerm}
                onChange={(e) => setCampaignTerm(e.target.value)}
                placeholder={t.campaignTermPlaceholder}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              />
              <p className="text-xs text-gray-500 mt-2">{t.campaignTermHint}</p>
            </div>

            {/* Campaign Content (Optional) */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="campaignContent" className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                {t.campaignContentLabel}
              </label>
              <input
                type="text"
                id="campaignContent"
                value={campaignContent}
                onChange={(e) => setCampaignContent(e.target.value)}
                placeholder={t.campaignContentPlaceholder}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              />
              <p className="text-xs text-gray-500 mt-2">{t.campaignContentHint}</p>
            </div>

            {/* Live Preview Toggle */}
            {(websiteUrl.trim() && campaignSource.trim() && campaignMedium.trim() && campaignName.trim()) && (
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm font-semibold text-gray-900">{t.previewLabel}</span>
                </label>
              </div>
            )}

            {/* Live Preview */}
            {showPreview && websiteUrl.trim() && campaignSource.trim() && campaignMedium.trim() && campaignName.trim() && (
              <div className="mb-6 p-5 bg-green-50 rounded-xl border-2 border-green-200">
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    {t.paramsLabel}
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="px-3 py-1.5 bg-white border border-green-200 rounded-lg text-xs font-mono text-gray-800">
                      <span className="text-green-600 font-semibold">utm_source:</span> {campaignSource}
                    </span>
                    <span className="px-3 py-1.5 bg-white border border-green-200 rounded-lg text-xs font-mono text-gray-800">
                      <span className="text-green-600 font-semibold">utm_medium:</span> {campaignMedium}
                    </span>
                    <span className="px-3 py-1.5 bg-white border border-green-200 rounded-lg text-xs font-mono text-gray-800">
                      <span className="text-green-600 font-semibold">utm_campaign:</span> {campaignName}
                    </span>
                    {campaignTerm.trim() && (
                      <span className="px-3 py-1.5 bg-white border border-green-200 rounded-lg text-xs font-mono text-gray-800">
                        <span className="text-green-600 font-semibold">utm_term:</span> {campaignTerm}
                      </span>
                    )}
                    {campaignContent.trim() && (
                      <span className="px-3 py-1.5 bg-white border border-green-200 rounded-lg text-xs font-mono text-gray-800">
                        <span className="text-green-600 font-semibold">utm_content:</span> {campaignContent}
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-white border-2 border-green-300 rounded-lg p-4">
                  <code className="text-xs sm:text-sm text-gray-800 break-all font-mono">{buildUTMLink()}</code>
                </div>
                <p className="text-xs text-center text-gray-600 mt-3">{t.previewHint}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={generateUTMLink}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold py-3 sm:py-4 px-6 rounded-lg sm:rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
              >
                <Link2 className="w-5 h-5" />
                {t.generateButton}
              </button>
              <button
                onClick={clearAll}
                className="px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 font-medium rounded-lg sm:rounded-xl hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                {t.clearButton}
              </button>
            </div>
          </div>

          {/* Generated Link Section */}
          {generatedLink && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 border border-gray-100 animate-[fadeIn_0.5s_ease-out] mb-6 sm:mb-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t.generatedTitle}</h2>
              </div>

              {/* UTM Parameters Visualization */}
              <div className="mb-4 sm:mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-green-600" />
                  {t.paramsLabel}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(parseUTMParams(generatedLink)).map(([key, value]) => (
                    <span key={key} className="px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs font-mono text-gray-800">
                      <span className="text-green-600 font-semibold">{key}:</span> {value}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-green-50/30 border-2 border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 mb-4 sm:mb-6 group hover:border-green-300 transition-all duration-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-green-600 animate-pulse"></div>
                  </div>
                  <code className="text-xs sm:text-sm text-gray-800 break-all font-mono flex-1">{generatedLink}</code>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="mb-4 sm:mb-6">
                <button
                  onClick={() => setShowQR(!showQR)}
                  className="w-full bg-white border-2 border-green-600 text-green-600 font-semibold py-3 px-6 rounded-lg hover:bg-green-50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <QrCode className="w-5 h-5" />
                  {showQR ? (language === 'tr' ? 'QR Kodu Gizle' : language === 'en' ? 'Hide QR Code' : 'Скрыть QR-код') : t.qrLabel}
                </button>
                {showQR && (
                  <div className="mt-4 p-6 bg-gray-50 rounded-xl border-2 border-green-200 flex flex-col items-center">
                    <div id="qr-code" className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block mb-4">
                      <QRCodeSVG
                        value={generatedLink}
                        size={200}
                        level="M"
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        includeMargin={true}
                      />
                    </div>
                    <button
                      onClick={downloadQR}
                      className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      {t.qrDownload}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <button
                  onClick={copyToClipboard}
                  className={`font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base ${
                    copied 
                      ? 'bg-gradient-to-r from-green-600 to-green-700 text-white' 
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                  }`}
                >
                  <Copy className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${copied ? 'animate-bounce' : ''}`} />
                  {copied ? t.copied : t.copyButton}
                </button>
                <button
                  onClick={testLink}
                  className="bg-white border-2 border-green-600 text-green-600 font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl hover:bg-green-600 hover:text-white transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm sm:text-base"
                >
                  <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  {t.testButton}
                </button>
              </div>
            </div>
          )}

          {/* Link History */}
          {linkHistory.length > 0 && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 mb-6 sm:mb-8 border border-gray-100">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <History className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t.historyLabel}</h2>
                </div>
                <button
                  onClick={clearHistory}
                  className="text-xs sm:text-sm text-gray-500 hover:text-red-600 transition-colors"
                >
                  {t.historyClear}
                </button>
              </div>
              <div className="space-y-2">
                {linkHistory.map((link, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-200 group"
                  >
                    <button
                      onClick={() => useHistoryLink(link)}
                      className="flex-1 text-left"
                    >
                      <code className="text-xs sm:text-sm text-gray-700 break-all font-mono group-hover:text-green-700 transition-colors">
                        {link}
                      </code>
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(link)
                        setCopied(true)
                        setTimeout(() => setCopied(false), 2000)
                      }}
                      className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                      title={t.copyButton}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {t.faqs && t.faqs.length > 0 && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 mt-6 sm:mt-8 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center flex-shrink-0">
                  <Link2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t.faqTitle}</h2>
              </div>
              <FAQAccordion 
                faqs={t.faqs.map((faq: any) => ({
                  question: faq.question,
                  answer: faq.answer
                }))}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default UTMLinkGenerator

